import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api, { getActiveOperatingGroupId, OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../api/api';
import { useSelectedOperatingGroup } from '../hooks/useSelectedOperatingGroup';

export interface B52ModalItem {
  id: number;
  name: string;
  b52Code: string;
  b52Name: string;
  price?: number;
  b52Price?: number | null;
  finalPrice?: number;
}

interface B52ModalProps {
  title: string;
  entityType: 'products' | 'semifinished' | 'dishes';
  onClose: () => void;
  onSaved: () => void;
}

type B52ModalRow = B52ModalItem & {
  b52PriceInput: string;
};

type B52ManualEntry = { id: number; code: string; name: string; price: number | null };

const DATA_SHEET = 'B52';
const MANUAL_SHEET = 'Ручні записи B52';
const INSTRUCTION_SHEET = 'Інструкція';

const readCell = (row: any, ...keys: string[]) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== '') return String(row[key]).trim();
  }
  return '';
};

const parseNullableDecimal = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const B52Modal: React.FC<B52ModalProps> = ({ title, entityType, onClose, onSaved }) => {
  const selection = useSelectedOperatingGroup();
  const initialGroup = useRef(selection.groupId);
  const [invalidated, setInvalidated] = useState(false);
  const stale = useRef(false);
  const mounted = useRef(true);
  if (initialGroup.current === null && !stale.current) initialGroup.current = selection.groupId;
  const contextChanged = invalidated || selection.changedInAnotherTab
    || (initialGroup.current !== null && selection.groupId !== initialGroup.current);
  if (contextChanged) stale.current = true;
  const [rows, setRows] = useState<B52ModalRow[]>([]);
  const [rowsLoaded, setRowsLoaded] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const readOnly = contextChanged || selection.groupId === null || selection.groupId === 'all' || !rowsLoaded || !canEdit;
  const writable = useRef(!readOnly);
  writable.current = mounted.current && !stale.current && !readOnly;
  useEffect(() => {
    mounted.current = true;
    const invalidate = () => {
      writable.current = false;
      stale.current = true;
      setInvalidated(true);
    };
    const groupChanged = () => {
      const groupId = getActiveOperatingGroupId();
      if (initialGroup.current !== null && groupId !== initialGroup.current) invalidate();
    };
    const storageChanged = (event: StorageEvent) => {
      if (event.key === OPERATING_GROUP_STORAGE_KEY && event.newValue !== initialGroup.current
        && initialGroup.current !== null) invalidate();
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, groupChanged);
    window.addEventListener('storage', storageChanged);
    return () => {
      writable.current = false;
      mounted.current = false;
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, groupChanged);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);
  const hasSalePrice = entityType === 'products' || entityType === 'dishes';
  const salePriceLabel = entityType === 'dishes' ? 'Ціна страви' : 'Ціна продажу';
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [manualEntries, setManualEntries] = useState<B52ManualEntry[]>([]);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ code: '', name: '', price: '' });
  const [manualBusy, setManualBusy] = useState(false);

  const loadManual = React.useCallback(async () => {
    try {
      const res = await api.get<B52ManualEntry[]>(`/b52/manual/${entityType}`);
      if (stale.current || !mounted.current) return;
      setManualEntries(res.data ?? []);
    } catch (error: any) {
      if (stale.current || !mounted.current) throw error;
      setMsg(`❌ Не вдалося завантажити довідник B52: ${error?.response?.data?.message || error?.message || 'Невідома помилка'}`);
      throw error;
    }
  }, [entityType]);

  useEffect(() => {
    if (contextChanged || selection.groupId === null || selection.groupId !== getActiveOperatingGroupId()) return;
    let active = true;
    const path = entityType === 'semifinished' ? '/semifinishedproducts' : `/${entityType}`;
    // Never seed a newly opened modal from a Redux list loaded in another group.
    Promise.all([
      api.get<B52ModalItem[]>(path),
      api.get<B52ManualEntry[]>(`/b52/manual/${entityType}`),
      api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access'),
    ]).then(([catalogue, manual, access]) => {
      if (!active || stale.current || !mounted.current) return;
      if (!Array.isArray(catalogue.data) || !Array.isArray(manual.data)
        || !Array.isArray(access.data.permissions) || !Array.isArray(access.data.editPermissions))
        throw new Error('Некоректна відповідь довідника B52.');
      const permission = `page.management.${entityType === 'semifinished' ? 'semi-finished' : entityType}`;
      setCanEdit(access.data.permissions.includes(permission) && access.data.editPermissions.includes(permission));
      setRows(catalogue.data.map(i => ({
        ...i,
        b52Code: i.b52Code ?? '',
        b52Name: i.b52Name ?? '',
        price: Number((entityType === 'dishes' ? i.finalPrice : i.price) ?? 0),
        b52Price: i.b52Price ?? null,
        b52PriceInput: i.b52Price === null || i.b52Price === undefined ? '' : String(i.b52Price),
      })));
      setManualEntries(manual.data);
      setRowsLoaded(true);
    }).catch((error) => {
      if (active && !stale.current && mounted.current)
        setMsg(`❌ Не вдалося завантажити довідник B52: ${error?.response?.data?.message || error?.message || 'Невідома помилка'}`);
    });
    return () => { active = false; };
  }, [entityType, contextChanged, selection.groupId]);

  const upsertManual = async (entries: Array<{ code: string; name: string; price: number | null }>) => {
    if (!writable.current) throw new Error('Робочий контекст змінився або доступний лише для читання.');
    const res = await api.post<{ added: number; updated: number }>(`/b52/manual/${entityType}`, entries);
    if (writable.current) await loadManual();
    return res.data;
  };

  const handleAddManual = async () => {
    if (!writable.current) return;
    const code = manualForm.code.trim();
    const name = manualForm.name.trim();
    if (!code || !name) { setMsg('❌ Вкажіть код B52 і назву B52'); return; }
    const price = hasSalePrice ? parseNullableDecimal(manualForm.price) : null;
    if (hasSalePrice && manualForm.price.trim() && (price === null || price < 0)) { setMsg('❌ Некоректна ціна B52'); return; }
    setManualBusy(true);
    setMsg(null);
    try {
      const result = await upsertManual([{ code, name, price }]);
      if (!writable.current) return;
      setManualForm({ code: '', name: '', price: '' });
      setMsg(result.updated > 0 ? `✅ Запис B52 ${code} оновлено` : `✅ Запис B52 ${code} додано до довідника`);
    } catch (e: any) {
      setMsg(`❌ Помилка: ${e?.response?.data?.message || e?.message || 'Невідома помилка'}`);
    } finally {
      setManualBusy(false);
    }
  };

  const handleDeleteManual = async (entry: B52ManualEntry) => {
    if (!writable.current) return;
    if (!window.confirm(`Видалити запис B52 «${entry.code} — ${entry.name}» з довідника?`)) return;
    setManualBusy(true);
    try {
      await api.delete(`/b52/manual/${entityType}/${entry.id}`);
      if (writable.current) await loadManual();
    } catch (e: any) {
      setMsg(`❌ Помилка: ${e?.response?.data?.message || e?.message || 'Невідома помилка'}`);
    } finally {
      setManualBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q)
      || r.b52Code.toLowerCase().includes(q)
      || r.b52Name.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const updateRow = (id: number, field: 'b52Code' | 'b52Name' | 'b52PriceInput', value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    if (!writable.current) return;
    setSaving(true);
    setMsg(null);
    try {
      const updates = buildUpdates();
      await api.post(`/b52/bulk-update/${entityType}`, updates);
      if (!writable.current) return;
      setMsg('✅ Збережено!');
      onSaved();
    } catch (e: any) {
      setMsg(`❌ Помилка: ${e?.response?.data?.message || e?.message || 'Невідома помилка'}`);
    } finally {
      setSaving(false);
    }
  };

  const buildUpdates = () => {
    for (const row of rows) {
      const price = parseNullableDecimal(row.b52PriceInput);
      if (hasSalePrice && row.b52PriceInput.trim() && (price === null || price < 0))
        throw new Error(`Некоректна ціна B52: ${row.name}`);
    }
    return rows.map((r) => ({
        id: r.id,
        b52Code: r.b52Code.trim(),
        b52Name: r.b52Name.trim(),
        ...(hasSalePrice ? { b52Price: parseNullableDecimal(r.b52PriceInput) } : {}),
      }));
  };

  const handleExport = () => {
    const exportRows = rows.map((r) => ({
      'Код (ID)': r.id,
      'Назва': r.name,
      'Код B52': r.b52Code,
      'Назва B52': r.b52Name,
      ...(hasSalePrice ? { 'Ціна B52': r.b52Price ?? '' } : {}),
      ...(hasSalePrice ? { [salePriceLabel]: r.price ?? 0 } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    ws['!cols'] = hasSalePrice
      ? [{ wch: 8 }, { wch: 40 }, { wch: 16 }, { wch: 30 }, { wch: 14 }, { wch: 14 }]
      : [{ wch: 8 }, { wch: 40 }, { wch: 16 }, { wch: 30 }];
    const manualHeader = hasSalePrice ? ['Код B52', 'Назва B52', 'Ціна B52'] : ['Код B52', 'Назва B52'];
    const manualWs = XLSX.utils.aoa_to_sheet([
      manualHeader,
      ...manualEntries.map((m) => (hasSalePrice ? [m.code, m.name, m.price ?? ''] : [m.code, m.name])),
    ]);
    manualWs['!cols'] = [{ wch: 16 }, { wch: 40 }, { wch: 14 }];
    const priceColumns = hasSalePrice
      ? [
        ['• «Ціна B52» — ціна з системи B52 (число, крапка або кома як роздільник). Порожньо = без ціни.'],
        [`• «${salePriceLabel}» — лише для довідки, при імпорті ігнорується. Щоб перенести ціни B52 у CRM, після імпорту натисніть «Встановити ціни з B52».`],
      ]
      : [];
    const instructionWs = XLSX.utils.aoa_to_sheet([
      [`Інструкція з імпорту B52 довідника — ${title}`],
      [''],
      ['1. Не перейменовуйте аркуші «B52» та «Ручні записи B52» і не змінюйте назви колонок у першому рядку.'],
      ['2. Аркуш «B52» — прив\'язка кодів B52 до позицій CRM:'],
      ['• «Код (ID)» — ID позиції в CRM. Не змінюйте його. Якщо ID порожній, позиція шукається за точною «Назвою».'],
      ['• «Назва» — назва позиції в CRM (лише для орієнтира, при імпорті не змінюється).'],
      ['• «Код B52» і «Назва B52» — впишіть або виправте значення з B52. Порожня клітинка очистить код/назву B52 у позиції.'],
      ...priceColumns,
      ['• Усі позиції мають належати поточній групі. Недоступний, невідомий або повторний ID зупиняє імпорт без збереження. Нові позиції CRM цим файлом не створюються.'],
      ['3. Аркуш «Ручні записи B52» — окремі коди B52 без прив\'язки до позицій CRM (з\'являються в підказках полів «Код B52»/«Назва B52»):'],
      ['• Кожен рядок: «Код B52» і «Назва B52» обов\'язкові' + (hasSalePrice ? ', «Ціна B52» — за бажанням.' : '.')],
      ['• Якщо код уже є в довіднику — його назва/ціна оновляться; якщо немає — запис буде додано. Видалення через файл не відбувається.'],
      [''],
      ['4. Збережіть файл (.xlsx) і в CRM відкрийте «B52 довідник» → «Імпорт Excel».'],
      ['• Ручні записи зберігаються одразу після імпорту.'],
      ['• Зміни прив\'язок з аркуша «B52» показуються в таблиці — перевірте їх і натисніть «Зберегти зміни».'],
    ]);
    instructionWs['!cols'] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, DATA_SHEET);
    XLSX.utils.book_append_sheet(wb, manualWs, MANUAL_SHEET);
    XLSX.utils.book_append_sheet(wb, instructionWs, INSTRUCTION_SHEET);
    XLSX.writeFile(wb, `b52_${entityType}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!writable.current) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setMsg(null);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        if (!writable.current) throw new Error('Робочий контекст змінився.');
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const dataSheetName = wb.SheetNames.find((n: string) => n.trim() === DATA_SHEET)
          ?? wb.SheetNames.find((n: string) => n.trim() !== MANUAL_SHEET && n.trim() !== INSTRUCTION_SHEET);
        const manualSheetName = wb.SheetNames.find((n: string) => n.trim() === MANUAL_SHEET);
        const ws = dataSheetName ? wb.Sheets[dataSheetName] : undefined;
        const fileRows: any[] = ws ? XLSX.utils.sheet_to_json(ws, { defval: '' }) : [];

        const manualRows: any[] = manualSheetName ? XLSX.utils.sheet_to_json(wb.Sheets[manualSheetName], { defval: '' }) : [];
        const manualToSave: Array<{ code: string; name: string; price: number | null }> = [];
        const manualCodes = new Set<string>();
        for (const row of manualRows) {
          const code = readCell(row, 'Код B52', 'code');
          const name = readCell(row, 'Назва B52', 'name');
          if (!code && !name) continue;
          if (!code || !name) throw new Error('Для кожного ручного запису потрібні код B52 і назва.');
          if (manualCodes.has(code.toLowerCase())) throw new Error(`Повторний код B52: ${code}`);
          manualCodes.add(code.toLowerCase());
          const rawPrice = row['Ціна B52'] ?? row.price ?? '';
          const price = hasSalePrice ? parseNullableDecimal(rawPrice) : null;
          if (hasSalePrice && String(rawPrice).trim() && (price === null || price < 0))
            throw new Error(`Некоректна ціна B52: ${code}`);
          manualToSave.push({ code, name, price });
        }
        let manualMsg = '';

        let updated = 0;
        const next = rows.map((r) => ({ ...r }));
        const matched = new Set<number>();
        for (const row of fileRows) {
          const rawId = String(row['Код (ID)'] ?? row.id ?? '').trim();
          const id = Number(rawId);
          const name = String(row['Назва'] ?? row.name ?? '').trim().toLowerCase();
          const b52Code = String(row['Код B52'] ?? row.b52Code ?? '').trim();
          const b52Name = String(row['Назва B52'] ?? row.b52Name ?? '').trim();
          const b52PriceRaw = row['Ціна B52'] ?? row.b52Price ?? '';
          const parsedB52Price = parseNullableDecimal(b52PriceRaw);
          if (rawId && (!Number.isInteger(id) || id <= 0))
            throw new Error(`Некоректний ID позиції CRM: ${rawId}`);
          if (hasSalePrice && String(b52PriceRaw).trim() && (parsedB52Price === null || parsedB52Price < 0))
            throw new Error(`Некоректна ціна B52: ${name || rawId}`);

          if (!rawId && next.filter(r => r.name.toLowerCase() === name).length !== 1)
            throw new Error(`Позицію CRM не знайдено однозначно: ${name}`);
          const idx = rawId
            ? next.findIndex((r) => r.id === id)
            : next.findIndex((r) => r.name.toLowerCase() === name);
          if (idx < 0) throw new Error(`Позиція CRM недоступна у поточному довіднику: ${rawId || name}`);
          if (matched.has(idx)) throw new Error(`Повторна позиція CRM: ${rawId || name}`);
          matched.add(idx);

          if (idx >= 0) {
            next[idx] = {
              ...next[idx],
              b52Code,
              b52Name,
              ...(hasSalePrice
                ? {
                  b52Price: parsedB52Price,
                  b52PriceInput: parsedB52Price === null ? '' : String(parsedB52Price),
                }
                : {}),
            };
            updated++;
          }
        }
        if (manualToSave.length > 0) {
          const result = await upsertManual(manualToSave);
          manualMsg = ` Ручні записи B52: додано ${result.added}, оновлено ${result.updated} (збережено).`;
        }
        if (!writable.current) throw new Error('Робочий контекст змінився.');
        setRows(next);
        setMsg(`📥 Імпортовано з файлу: оновлено ${updated} позицій — натисніть "Зберегти зміни" для запису в базу.${manualMsg}`);
      } catch (err: any) {
        setMsg(`❌ Помилка імпорту файлу: ${err?.response?.data?.message || err?.message || 'невідома помилка'}`);
      } finally {
        setImporting(false);
      }
    };
    reader.onerror = () => {
      setMsg('❌ Не вдалося прочитати файл Excel.');
      setImporting(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const isSuccess = (m: string) => m.startsWith('✅') || m.startsWith('📥');
  const canApplySalePrices = entityType === 'products' || entityType === 'dishes';

  const handleApplySalePrices = async () => {
    if (!writable.current) return;
    setSaving(true);
    setMsg(null);
    let codesSaved = false;
    try {
      const updates = buildUpdates();
      await api.post(`/b52/bulk-update/${entityType}`, updates);
      codesSaved = true;
      if (!writable.current) throw new Error('Робочий контекст змінився. Ціни не застосовано.');
      const response = await api.post(`/b52/apply-sale-prices/${entityType}`);
      if (!writable.current) return;
      const updatedCount = Number(response?.data?.updated ?? 0);
      setRows((prev) => prev.map((row) => ({
        ...row,
        price: parseNullableDecimal(row.b52PriceInput) ?? row.price ?? 0,
      })));
      setMsg(`✅ Ціни оновлено з B52: ${updatedCount}`);
      onSaved();
    } catch (e: any) {
      setMsg(`❌ ${codesSaved ? 'Прив’язки B52 збережено; застосування цін не підтверджено. ' : ''}Помилка: ${e?.response?.data?.message || e?.message || 'Невідома помилка'}`);
    } finally {
      setSaving(false);
    }
  };

  if (contextChanged) return (
    <div role="alert">
      Робочу групу змінено. Закрийте довідник і відкрийте його знову після оновлення даних.
      <button type="button" onClick={onClose}>Закрити</button>
    </div>
  );

  return (
    <div className="crm-mobile-modal-shell" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
      <div className="crm-mobile-modal-card" style={{ width: '100%', maxWidth: '980px', background: 'white', borderRadius: '10px', boxShadow: '0 10px 40px rgba(0,0,0,.25)', marginTop: '20px', marginBottom: '20px' }}>
        <div className="b52-modal-header" style={{ background: '#2c3e50', color: 'white', padding: '14px 20px', borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>B52 Довідник — {title}</h3>
          <button className="b52-modal-close" type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div className="b52-modal-toolbar" style={{ padding: '10px 20px', borderBottom: '1px solid #ecf0f1', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || readOnly}
            className="b52-modal-action"
            style={{ background: saving ? '#bdc3c7' : '#27ae60', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '13px' }}
          >
            {saving ? 'Збереження...' : '💾 Зберегти зміни'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="b52-modal-action"
            style={{ background: '#16a085', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}
          >
            ⬇ Експорт Excel
          </button>
          {canApplySalePrices && (
            <button
              type="button"
              onClick={handleApplySalePrices}
              disabled={saving || readOnly}
              className="b52-modal-action"
              style={{ background: saving ? '#bdc3c7' : '#d35400', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '13px' }}
            >
              Встановити ціни з B52
            </button>
          )}
          <label className="b52-modal-action" style={{ background: '#2980b9', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '13px', opacity: importing ? 0.7 : 1 }}>
            ⬆ {importing ? 'Імпорт...' : 'Імпорт Excel'}
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} disabled={importing || readOnly} style={{ display: 'none' }} />
          </label>
          <button
            type="button"
            onClick={() => setShowManual((v) => !v)}
            className="b52-modal-action"
            style={{ background: showManual ? '#6c3483' : '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}
          >
            ✚ Ручні записи ({manualEntries.length})
          </button>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук..."
            className="b52-modal-search"
            style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: '6px', border: '1px solid #dfe6e9', fontSize: '13px', minWidth: '160px' }}
          />
        </div>

        {readOnly && <p role="status" style={{ margin: '8px 20px' }}>
          {selection.groupId === null ? 'Підтвердження робочої групи. Зміни недоступні.'
            : selection.groupId === 'all' ? 'Усі групи — лише читання. Для змін оберіть робочу групу.'
              : rowsLoaded && !canEdit ? 'Лише перегляд. Для змін потрібне право «Редагування» цієї категорії.'
                : 'Завантаження актуального довідника. Зміни недоступні.'}
        </p>}
        {msg && (
          <div style={{ margin: '8px 20px', padding: '7px 12px', borderRadius: '6px', background: isSuccess(msg) ? '#d5f5e3' : '#fdecea', color: isSuccess(msg) ? '#1e8449' : '#c0392b', fontSize: '13px' }}>
            {msg}
          </div>
        )}

        {showManual && (
          <fieldset disabled={readOnly || manualBusy} style={{ margin: '8px 20px', border: '1px solid #d7bde2', borderRadius: '8px', background: '#f9f3fc', padding: '10px 12px', minWidth: 0 }}>
            <div style={{ fontSize: '12px', color: '#5b2c6f', marginBottom: '8px' }}>
              Коди B52, яких ще немає в жодній позиції CRM. Вони з'являться в підказках полів «Код B52» / «Назва B52» під час редагування. Якщо ввести вже наявний код — назва{hasSalePrice ? ' і ціна' : ''} оновляться.
            </div>
            <div className="b52-modal-manual-form" style={{ display: 'grid', gridTemplateColumns: hasSalePrice ? '140px 1fr 110px auto' : '140px 1fr auto', gap: '8px', marginBottom: '8px' }}>
              <input value={manualForm.code} onChange={(e) => setManualForm({ ...manualForm, code: e.target.value })} placeholder="Код B52" style={{ padding: '6px 8px', border: '1px solid #dfe6e9', borderRadius: '5px', fontSize: '13px' }} />
              <input value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleAddManual(); }} placeholder="Назва B52" style={{ padding: '6px 8px', border: '1px solid #dfe6e9', borderRadius: '5px', fontSize: '13px' }} />
              {hasSalePrice && <input value={manualForm.price} onChange={(e) => setManualForm({ ...manualForm, price: e.target.value })} placeholder="Ціна B52" style={{ padding: '6px 8px', border: '1px solid #dfe6e9', borderRadius: '5px', fontSize: '13px' }} />}
              <button className="b52-modal-action" type="button" onClick={handleAddManual} disabled={manualBusy} style={{ background: manualBusy ? '#bdc3c7' : '#8e44ad', color: 'white', border: 'none', borderRadius: '5px', padding: '6px 14px', cursor: manualBusy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                {manualBusy ? '...' : 'Додати'}
              </button>
            </div>
            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {manualEntries.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#7f8c8d', padding: '6px 0' }}>Ручних записів ще немає.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <tbody>
                    {manualEntries
                      .filter((m) => !search.trim() || `${m.code} ${m.name}`.toLowerCase().includes(search.trim().toLowerCase()))
                      .map((m) => (
                        <tr key={m.id} style={{ borderBottom: '1px solid #ebdef0' }}>
                          <td style={{ padding: '4px 6px', fontWeight: 600, width: '140px' }}>{m.code}</td>
                          <td style={{ padding: '4px 6px' }}>{m.name}</td>
                          {hasSalePrice && <td style={{ padding: '4px 6px', width: '90px', textAlign: 'right' }}>{m.price != null ? Number(m.price).toFixed(2) : '—'}</td>}
                          <td style={{ padding: '4px 6px', width: '90px', textAlign: 'right' }}>
                            <button type="button" onClick={() => setManualForm({ code: m.code, name: m.name, price: m.price != null ? String(m.price) : '' })} title="Редагувати" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✎</button>
                            <button type="button" onClick={() => handleDeleteManual(m)} disabled={manualBusy} title="Видалити" style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }}>×</button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </fieldset>
        )}

        <fieldset disabled={readOnly} className="crm-mobile-modal-content" style={{ maxHeight: 'calc(75vh - 140px)', overflowY: 'auto', padding: '0 20px 16px', border: 0, minWidth: 0 }}>
          <table className="crm-mobile-table-scroll" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '8px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: hasSalePrice ? '32%' : '38%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: hasSalePrice ? '30%' : '46%' }} />
              {hasSalePrice && <col style={{ width: '11%' }} />}
              {hasSalePrice && <col style={{ width: '11%' }} />}
            </colgroup>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>Назва</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>Код B52</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>Назва B52</th>
                {hasSalePrice && <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>Ціна B52</th>}
                {hasSalePrice && <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>{salePriceLabel}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={hasSalePrice ? 5 : 3} style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>Нічого не знайдено</td></tr>
              )}
              {filtered.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #f1f2f6' }}>
                  <td style={{ padding: '5px 8px', color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.name}>{row.name}</td>
                  <td style={{ padding: '3px 8px' }}>
                    <input
                      value={row.b52Code}
                      onChange={(e) => updateRow(row.id, 'b52Code', e.target.value)}
                      style={{ width: '100%', padding: '3px 6px', border: '1px solid #dfe6e9', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                      placeholder="Код B52"
                    />
                  </td>
                  <td style={{ padding: '3px 8px' }}>
                    <input
                      value={row.b52Name}
                      onChange={(e) => updateRow(row.id, 'b52Name', e.target.value)}
                      style={{ width: '100%', padding: '3px 6px', border: '1px solid #dfe6e9', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                      placeholder="Назва B52"
                    />
                  </td>
                  {hasSalePrice && (
                    <td style={{ padding: '3px 8px' }}>
                      <input
                        value={row.b52PriceInput}
                        onChange={(e) => updateRow(row.id, 'b52PriceInput', e.target.value)}
                        style={{ width: '100%', padding: '3px 6px', border: '1px solid #dfe6e9', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                        placeholder="0.00"
                      />
                    </td>
                  )}
                  {hasSalePrice && (
                    <td style={{ padding: '5px 8px', color: '#2c3e50', textAlign: 'right' }}>
                      {Number(row.price ?? 0).toFixed(2)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      </div>
    </div>
  );
};

export default B52Modal;
